import { MakeRequestOptions } from './types/MakeRequestOptions';
import { MakeRequestError } from './errors/MakeRequestError';
import { MakeRequestConfigOptions } from './types/MakeRequestConfigOptions';
import { v4 as uuidv4 } from 'uuid';
import qs from 'qs';

interface CancellablePromise<T> extends Promise<T> {
    cancel: () => void
}

let logFn: (level: any, message: string) => void;
let getHeaders: () => Promise<{ [key: string]: string }>;
let logOnUnsuccessfulStatusCode: boolean;

let config = {
    baseUrl: '',
    sessionId: ''
}

function log(level: any, message: string) {
    if (logFn) {
        logFn(level, message);
    }
}

export function configureMakeRequest(options: MakeRequestConfigOptions) {
    if (options.baseUrl) {
        config.baseUrl = options.baseUrl;
    }

    if (options.sessionId) {
        config.sessionId = options.sessionId;
    }

    if (options.log) {
        logFn = options.log;
    }

    if (options.addHeadersOnEachRequest) {
        getHeaders = options.addHeadersOnEachRequest;
    }

    if (options.logOnUnsuccessfulStatusCode) {
        logOnUnsuccessfulStatusCode = true;
    }
}

export function makeRequest(requestOptions: MakeRequestOptions) {
    const abortController = new AbortController();

    const promise = (async () => {
        let path = `${config.baseUrl}${requestOptions.path}`;
        const correlationId = uuidv4();

        let extraHeaders = {};
        if (getHeaders) {
            extraHeaders = await getHeaders();
        }

        let options = {
            signal: abortController.signal,
            method: requestOptions.method,
            headers: {
                'x-session-id': config.sessionId,
                'x-correlation-id': correlationId,
                ...extraHeaders
            } as Record<string, string>,
            body: undefined,
            timeout: requestOptions.timeout
        };

        if (requestOptions.raw !== true) {
            options.headers['Content-Type'] = 'application/json'
        }

        if (requestOptions.data && requestOptions.method === 'GET') {
            const queryString = qs.stringify(requestOptions.data, { allowDots: true });

            path += `?${queryString}`;

        } else if (requestOptions.data) {
            options.body = requestOptions.raw ? requestOptions.data : JSON.stringify(requestOptions.data) as string;
        }

        log('Info', `API: Request ${requestOptions.method} ${path} cid:${correlationId} sid:${config.sessionId}`);

        const timeoutId = requestOptions.timeout ? setTimeout(() => {
            abortController.abort();
        }, requestOptions.timeout) : null;

        const response = await fetch(path, options);

        if(timeoutId) {
            clearTimeout(timeoutId);
        }

        let responseLogMessage = `API: Response ${response.status} ${requestOptions.method} ${path} cid:${correlationId} sid:${config.sessionId}`;

        if (response.status < 200 || response.status >= 400) {
            const responseText = await response.text();

            if (logOnUnsuccessfulStatusCode) {
                responseLogMessage += ` body:${responseText}`;
            }

            log(
                'Error',
                responseLogMessage
            );

            let responseBody;
            try {
                responseBody = JSON.parse(responseText);
            } catch {
                responseBody = responseText;
            }

            throw new MakeRequestError(response.statusText, response.status, responseBody);
        } else {
            log(
                'Info',
                responseLogMessage
            );
        }

        if (requestOptions.responseType != undefined
            && requestOptions.responseType == 'buffer') {
            return response;
        }
        else if (requestOptions.responseType != undefined
            && requestOptions.responseType == 'text') {
            return response.text();
        }

        let returnValue = null;
        try {
            returnValue = await parseResponse(response, path, options);
        } catch (e) {
            log(
                'Error',
                `API: Unable to parse response for: ${response.status} ${requestOptions.method} ${path} cid:${correlationId} sid:${config.sessionId}`
            );
            throw new Error('ParseError');
        }

        return returnValue;
    })() as CancellablePromise<any>;

    promise.cancel = () => {
        abortController.abort();
    };

    return promise;
}

async function parseResponse(response: any, path: string, options: any) {
    const text = await response.text();
    if (text) {
        return JSON.parse(text);
    } else {
        return null;
    }
}
